import React, { useState, useRef, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseConfig';
import { renderMarkdown } from '../../utils/markdown';

const callClaude = httpsCallable(functions, 'claude');

const NODE_LABELS = {
  functionNode:   'Function',
  agentNode:      'Agent',
  gateNode:       'Gate',
  collectionNode: 'Collection',
};

function buildSystemPrompt(nodeType, nodeName, pipelineContext) {
  const existingNodes = (pipelineContext?.nodes || [])
    .filter(n => n.type !== 'noteNode')
    .map(n => `  - [${n.type}] id:"${n.id}" name:"${n.data?.label || 'Unnamed'}"${n.data?.description ? `: ${n.data.description}` : ''}`)
    .join('\n');

  const baseContext = existingNodes
    ? `\nCURRENT PIPELINE NODES:\n${existingNodes}`
    : '';

  const functionNodes = (pipelineContext?.nodes || [])
    .filter(n => n.type === 'functionNode')
    .map(n => `  - id:"${n.id}" name:"${n.data?.label || 'Unnamed'}"`)
    .join('\n');

  const collectionNodes = (pipelineContext?.nodes || [])
    .filter(n => n.type === 'collectionNode')
    .map(n => `  - id:"${n.id}" name:"${n.data?.label || 'Unnamed'}"`)
    .join('\n');

  const typeInstructions = {
    functionNode: `You are generating configuration for a FUNCTION NODE. A function is a team of AI agents that work together on a specific domain (e.g. Marketing, Service Desk, Development).

Ask the user:
1. What domain or responsibility this function covers
2. What kinds of tasks it should handle
3. How many agents it should have and what their roles are
4. Should this function connect to any existing data collection? (optional)
${collectionNodes ? `\nAVAILABLE DATA COLLECTIONS TO CONNECT TO:\n${collectionNodes}\nUse the exact id value for connectToNodeId, or null if no connection needed.` : ''}

When you have enough information (usually after 2-3 exchanges), generate a config. Output EXACTLY this JSON block (no other text after it):

<CONFIG>
{
  "label": "Function name",
  "description": "Brief description of what this function does",
  "playbook": "Detailed operating instructions agents follow — how to handle tasks, escalation rules, tone, what to do when stuck, and any key processes for this function",
  "contextNotes": "Extra context, constraints, external links, or edge cases agents should know about",
  "connectToNodeId": null,
  "agents": [
    {
      "id": "a1",
      "name": "Agent first name",
      "role": "Agent role description",
      "isManager": true,
      "model": "standard",
      "instructions": "Detailed instructions for this agent's role and behaviour"
    }
  ]
}
</CONFIG>

Model choices: "fast" (Haiku — simple/repetitive tasks), "standard" (Sonnet — general purpose), "smart" (Opus — complex reasoning). The first agent should be isManager: true.
IMPORTANT: For connectToNodeId, use the exact id string (e.g. "node-2") from the list above, NOT the name. Set to null if no connection.`,

    agentNode: `You are generating configuration for an AGENT within a function. Ask the user:
1. What this agent's specific role and responsibilities are
2. What tasks they handle day-to-day
3. How they should communicate and make decisions

When ready, output EXACTLY:

<CONFIG>
{
  "name": "Agent first name",
  "role": "Short role title",
  "instructions": "Detailed multi-paragraph instructions for the agent covering: role, responsibilities, communication style, decision boundaries, escalation rules",
  "model": "standard",
  "emoji": "👤"
}
</CONFIG>

Model choices: "fast", "standard", "smart".`,

    gateNode: `You are generating configuration for a GATE NODE. Gates are approval checkpoints — the pipeline pauses until a human approves. Ask:
1. What needs to be approved at this gate
2. Who approves it (e.g. manager, business owner, legal)
3. What criteria determine approval vs rejection

When ready, output EXACTLY:

<CONFIG>
{
  "label": "Gate name",
  "description": "What gets reviewed here and who approves it",
  "approver": "Role of the approver",
  "criteria": "What conditions must be met for approval"
}
</CONFIG>`,

    collectionNode: `You are generating configuration for a DATA COLLECTION. Collections are structured databases for storing business data (like tickets, contacts, tasks). Ask:
1. What kind of data this collection tracks
2. What fields are needed (name, type of data, whether it's a list/select)
3. Should it start with any sample/template records?
4. Which function node (if any) should this collection be connected to?
${functionNodes ? `\nAVAILABLE FUNCTION NODES TO CONNECT TO:\n${functionNodes}\nIMPORTANT: For connectToNodeId use the exact id string (e.g. "node-2"), NOT the name. Set to null if no connection needed.` : ''}

When ready, output EXACTLY:

<CONFIG>
{
  "label": "Collection name",
  "description": "What data this collection stores",
  "fields": [
    { "id": "f1", "name": "Field name", "type": "text" },
    { "id": "f2", "name": "Status", "type": "select", "options": ["Option A", "Option B"] },
    { "id": "f3", "name": "Linked record", "type": "relation", "targetCollectionId": null }
  ],
  "records": [
    { "id": "r1", "values": { "f1": "Example value", "f2": "Option A" } }
  ],
  "connectToNodeId": null
}
</CONFIG>

Field types: text, number, date, select (needs options array), relation (links to another collection), agent (stores agent name).
The records array uses the same field ids as the fields array. Include 2-5 sample records if the user asked for template data.
Set connectToNodeId to the exact node id string from the list above, or null.`,
  };

  return `${typeInstructions[nodeType] || typeInstructions.functionNode}
${baseContext}

CONVERSATION RULES:
- Be concise and friendly. Max 3-4 sentences per response unless asking multiple clarifying questions.
- Ask one or two questions at a time, not everything at once.
- Once you have enough detail, generate the config — don't ask unnecessary follow-up questions.
- When you output a <CONFIG> block, that is your final answer. Do not add anything after it.
${nodeName ? `\nThe user is editing an existing node called "${nodeName}" — factor this in when asking questions.` : ''}`;
}

function extractConfig(text) {
  const match = text.match(/<CONFIG>([\s\S]*?)<\/CONFIG>/);
  if (!match) return null;
  try { return JSON.parse(match[1].trim()); }
  catch { return null; }
}

function stripConfig(text) {
  return text.replace(/<CONFIG>[\s\S]*?<\/CONFIG>/g, '').trim();
}

const MODEL_LABELS = { fast: 'Efficient', standard: 'Balanced', smart: 'Expert' };

function ConfigPreview({ config, nodeType }) {
  if (!config) return null;
  return (
    <div className="gen-config-preview">
      <div className="gen-config-label">Ready to apply</div>
      <div className="gen-config-card">
        <div className="gen-config-name">{config.label || config.name || 'Unnamed'}</div>
        {config.description && <div className="gen-config-desc">{config.description}</div>}

        {nodeType === 'functionNode' && (config.agents?.length > 0) && (
          <div className="gen-config-section">
            <div className="gen-config-section-lbl">Agents</div>
            {config.agents.map((a, i) => (
              <div key={i} className="gen-config-agent-row">
                <span className="gen-config-agent-icon">{a.isManager ? '★' : '·'}</span>
                <span className="gen-config-agent-name">{a.name}</span>
                {a.role && <span className="gen-config-agent-role">{a.role}</span>}
                <span className={`gen-config-model-tag model-${a.model || 'standard'}`}>{MODEL_LABELS[a.model] || 'Balanced'}</span>
              </div>
            ))}
          </div>
        )}

        {nodeType === 'functionNode' && config.playbook && (
          <div className="gen-config-section">
            <div className="gen-config-section-lbl">Playbook</div>
            <div className="gen-config-preview-text">{config.playbook.slice(0, 120)}{config.playbook.length > 120 ? '…' : ''}</div>
          </div>
        )}

        {nodeType === 'collectionNode' && config.fields?.length > 0 && (
          <div className="gen-config-section">
            <div className="gen-config-section-lbl">Fields</div>
            {config.fields.map((f, i) => (
              <div key={i} className="gen-config-field-row">
                <span className="gen-config-field-name">{f.name}</span>
                <span className={`col-field-type type-${f.type}`}>{f.type}</span>
                {f.options?.length > 0 && <span className="gen-config-field-opts">{f.options.join(', ')}</span>}
              </div>
            ))}
          </div>
        )}

        {nodeType === 'collectionNode' && config.records?.length > 0 && (
          <div className="gen-config-section">
            <div className="gen-config-section-lbl">{config.records.length} sample record{config.records.length !== 1 ? 's' : ''} included</div>
          </div>
        )}

        {config.connectToNodeId && (
          <div className="gen-config-connect">
            Will auto-connect to function node on canvas
          </div>
        )}
      </div>
    </div>
  );
}

const draftKey = (nodeId) => `gen_draft_${nodeId}`;

function loadDraft(nodeId) {
  try {
    const raw = localStorage.getItem(draftKey(nodeId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveDraft(nodeId, messages, config) {
  try {
    localStorage.setItem(draftKey(nodeId), JSON.stringify({ messages, config }));
  } catch {}
}

function clearDraft(nodeId) {
  try { localStorage.removeItem(draftKey(nodeId)); } catch {}
}

export default function GeneratorModal({
  nodeType,
  nodeId,
  nodeName,
  pipelineContext,
  workspaceId,
  onApply,
  onClose,
  onMinimize,
  initialPos,
}) {
  const label = NODE_LABELS[nodeType] || 'Node';

  const welcome = `Hi! I'm the ${label} Generator 🤖 — I'll help you build a great configuration for this ${label.toLowerCase()} by asking a few quick questions.\n\nFirst up: ${
    nodeType === 'functionNode'   ? 'what domain or business area should this function cover? (e.g. "Marketing", "Service Desk", "Finance")' :
    nodeType === 'agentNode'      ? 'what role should this agent play within the team?' :
    nodeType === 'gateNode'       ? 'what kind of decision or approval does this gate represent?' :
    nodeType === 'collectionNode' ? 'what type of data should this collection track? (e.g. "support tickets", "customer contacts")' :
    'what would you like to configure?'
  }`;

  const draft = loadDraft(nodeId);
  const [messages, setMessages] = useState(draft?.messages || [{ role: 'assistant', text: welcome }]);
  const [input,    setInput]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [config,   setConfig]   = useState(draft?.config || null);
  const hasDraft = draft && draft.messages?.length > 1;

  const [pos,  setPos]  = useState(() => initialPos || {
    x: Math.max(20, window.innerWidth  - 540),
    y: Math.max(20, window.innerHeight - 660),
  });
  const [size, setSize] = useState({ width: 520, height: 640 });
  const isDragging  = useRef(false);
  const isResizing  = useRef(false);
  const dragStart   = useRef({});
  const resizeStart = useRef({});

  useEffect(() => {
    const onMove = (e) => {
      if (isDragging.current) {
        const { mouseX, mouseY, posX, posY } = dragStart.current;
        setPos({
          x: Math.max(0, Math.min(window.innerWidth  - size.width,  posX + e.clientX - mouseX)),
          y: Math.max(0, Math.min(window.innerHeight - 44,           posY + e.clientY - mouseY)),
        });
      }
      if (isResizing.current) {
        const { mouseX, mouseY, w, h } = resizeStart.current;
        setSize({ width: Math.max(380, w + e.clientX - mouseX), height: Math.max(280, h + e.clientY - mouseY) });
      }
    };
    const onUp = () => { isDragging.current = false; isResizing.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, [size.width]);

  const onDragStart = (e) => {
    if (e.target.closest('button') || e.button !== 0) return;
    e.preventDefault();
    isDragging.current = true;
    dragStart.current  = { mouseX: e.clientX, mouseY: e.clientY, posX: pos.x, posY: pos.y };
  };

  const onResizeStart = (e) => {
    e.stopPropagation(); e.preventDefault();
    isResizing.current = true;
    resizeStart.current = { mouseX: e.clientX, mouseY: e.clientY, w: size.width, h: size.height };
  };

  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Persist draft on every change
  useEffect(() => {
    if (messages.length > 1 || config) {
      saveDraft(nodeId, messages, config);
    }
  }, [messages, config, nodeId]);

  const handleReset = () => {
    clearDraft(nodeId);
    setMessages([{ role: 'assistant', text: welcome }]);
    setConfig(null);
  };

  const send = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');

    const userMsg = { role: 'user', text: msg };
    const history = [...messages, userMsg];
    setMessages(history);
    setLoading(true);

    try {
      const firstUserIdx = history.findIndex(m => m.role === 'user');
      const apiMessages = history
        .slice(firstUserIdx >= 0 ? firstUserIdx : 0)
        .slice(-8)
        .map(m => ({ role: m.role, content: m.text }));
      const result = await callClaude({
        messages:    apiMessages,
        model:       'claude-haiku-4-5-20251001',
        system:      buildSystemPrompt(nodeType, nodeName, pipelineContext),
        max_tokens:  900,
        workspaceId: workspaceId || 'unknown',
        purpose:     'generator',
      });

      const reply = result.data?.content?.[0]?.text ?? 'Sorry, something went wrong.';
      const extracted = extractConfig(reply);
      const displayText = extracted ? (stripConfig(reply) || `Great! I've generated the ${label} configuration. Review it below and click **Apply** when ready.`) : reply;

      setMessages(prev => [...prev, { role: 'assistant', text: displayText }]);
      if (extracted) setConfig(extracted);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: `Error: ${err?.message || 'Something went wrong. Please try again.'}`,
      }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, nodeType, nodeName, pipelineContext, workspaceId, label]);

  const handleApply = () => {
    if (config) {
      onApply(nodeId, config);
      clearDraft(nodeId);
    }
    onClose();
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div className="float-win gen-win" style={{ left: pos.x, top: pos.y, width: size.width, height: size.height }}>
      <div className="gen-hdr float-win-header" onMouseDown={onDragStart}>
        <span className="gen-icon">✨</span>
        <span className="gen-title">{label} Generator</span>
        {nodeName && <span className="gen-subtitle">editing: {nodeName}</span>}
        <div className="gen-hdr-actions float-win-actions">
          {(messages.length > 1 || config) && (
            <button className="float-btn gen-reset" onClick={handleReset} title="Start over">↺</button>
          )}
          {onMinimize && <button className="float-btn float-btn-minimize" onClick={onMinimize} title="Minimize">−</button>}
          <button className="float-btn float-btn-close" onClick={onClose} title="Close">✕</button>
        </div>
      </div>

      {hasDraft && (
        <div className="gen-draft-banner">
          Draft restored — your previous conversation was saved automatically.
        </div>
      )}

      <div className="gen-body">
        <div className="gen-messages">
          {messages.map((m, i) => (
            <div key={i} className={`gen-msg gen-msg-${m.role}`}>
              {m.role === 'assistant' && <span className="gen-avatar">✨</span>}
              <div className="gen-bubble">{renderMarkdown(m.text)}</div>
            </div>
          ))}
          {loading && (
            <div className="gen-msg gen-msg-assistant">
              <span className="gen-avatar">✨</span>
              <div className="gen-bubble gen-typing"><span /><span /><span /></div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {config && <ConfigPreview config={config} nodeType={nodeType} />}
      </div>

      <div className="gen-footer">
        <div className="gen-input-row">
          <textarea
            ref={inputRef}
            className="gen-input"
            placeholder="Type your answer…"
            value={input}
            rows={2}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
          />
          <button className="gen-send" onClick={() => send()} disabled={loading || !input.trim()}>
            Send
          </button>
        </div>
        {config && (
          <button className="gen-apply-btn" onClick={handleApply}>
            ✓ Apply to node
          </button>
        )}
      </div>

      <div className="float-resize-handle" onMouseDown={onResizeStart} />
    </div>
  );
}
